import React, { useEffect } from 'react'
import {
    Box, Typography, CircularProgress, Chip, Avatar,
    FormControl, InputLabel, Select, MenuItem, Paper,
    FormControlLabel, Switch
} from '@mui/material'
import { IconSeeker, CardSeeker } from '@ui-seeker/core'

import {
    selectedSku,
    skuDetailData,
    isLoading,
    selectedDays,
    marketFilter,
    onlyInStock,
    DAYS_OPTIONS,
    clearPriceAnalysisSignals
} from './@signals'
import usePriceAnalysis, { fetchSkuDetail } from './@hooks/usePriceAnalysis'
import SkuSelector from './SkuSelector'
import PriceAlertsTable from './PriceAlertsTable'
import MappingsTable from './MappingsTable'

export default function PriceAnalysis() {
    const { fetchSkuDetail } = usePriceAnalysis()

    // Cleanup on unmount
    useEffect(() => {
        return () => clearPriceAnalysisSignals()
    }, [])

    const sku = skuDetailData.value?.sku
    const mappings = skuDetailData.value?.mappings || []
    const priceAlerts = skuDetailData.value?.price_alerts || []

    // Get unique markets for filter
    const markets = [...new Set(mappings.map(m => m.market).filter(Boolean))].sort()

    // Filter mappings by market and stock
    let filteredMappings = mappings
    if (marketFilter.value) {
        filteredMappings = filteredMappings.filter(m => m.market === marketFilter.value)
    }
    if (onlyInStock.value) {
        filteredMappings = filteredMappings.filter(m => m.has_stock)
    }

    const handleDaysChange = (days) => {
        selectedDays.value = days
        if (selectedSku.value?.value) {
            fetchSkuDetail(selectedSku.value.value, days)
        }
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <IconSeeker icon="chart-line" size="lg" />
                <Typography variant="h5" fontWeight="bold">
                    Price Analysis
                </Typography>
            </Box>

            {/* SKU Selector */}
            <CardSeeker sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                    <SkuSelector />

                    {selectedSku.value && (
                        <>
                            {/* Date Filter */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconSeeker icon="calendar" size="sm" />
                                <Typography variant="body2" fontWeight="medium">Date Range:</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {DAYS_OPTIONS.map(opt => (
                                        <Chip
                                            key={opt.value}
                                            label={opt.label}
                                            onClick={() => handleDaysChange(opt.value)}
                                            color={selectedDays.value === opt.value ? 'primary' : 'default'}
                                            variant={selectedDays.value === opt.value ? 'filled' : 'outlined'}
                                            size="small"
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            </CardSeeker>

            {/* SKU Info Card */}
            {selectedSku.value && sku && (
                <CardSeeker sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Avatar
                            src={sku.image_url}
                            variant="rounded"
                            sx={{ width: 80, height: 80 }}
                        >
                            -
                        </Avatar>
                        <Box>
                            <Typography variant="h6" fontWeight="bold">
                                {sku.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                {sku.ean && (
                                    <Chip label={`EAN: ${sku.ean}`} size="small" variant="outlined" />
                                )}
                                {sku.reference && (
                                    <Chip label={`Ref: ${sku.reference}`} size="small" variant="outlined" />
                                )}
                                {sku.brand_name && (
                                    <Chip label={sku.brand_name} size="small" color="primary" variant="outlined" />
                                )}
                                {sku.line && (
                                    <Chip label={sku.line} size="small" variant="outlined" />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </CardSeeker>
            )}

            {/* Empty State */}
            {!selectedSku.value && (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <IconSeeker icon="magnifying-glass-chart" size="3x" sx={{ color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        Select a SKU to view price analysis
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Search by name, EAN or reference
                    </Typography>
                </Paper>
            )}

            {/* Loading State */}
            {isLoading.value && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Content */}
            {selectedSku.value && !isLoading.value && skuDetailData.value && (
                <Box>
                    {/* Price Alerts Section */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <IconSeeker icon="triangle-exclamation" size="sm" />
                            <Typography variant="subtitle1" fontWeight="bold">
                                Price Alerts by Market
                            </Typography>
                        </Box>
                        <PriceAlertsTable alerts={priceAlerts} />
                    </Box>

                    {/* Mappings Section */}
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconSeeker icon="list" size="sm" />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Mappings by Site
                                </Typography>
                                <Chip
                                    label={`${filteredMappings.length} products`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={onlyInStock.value}
                                            onChange={(e) => onlyInStock.value = e.target.checked}
                                            size="small"
                                        />
                                    }
                                    label="Only in stock"
                                />
                                {markets.length > 1 && (
                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <InputLabel>Filter by Market</InputLabel>
                                        <Select
                                            value={marketFilter.value || ''}
                                            onChange={(e) => marketFilter.value = e.target.value || null}
                                            label="Filter by Market"
                                        >
                                            <MenuItem value="">All Markets</MenuItem>
                                            {markets.map(market => (
                                                <MenuItem key={market} value={market}>{market}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Box>
                        </Box>
                        <MappingsTable mappings={filteredMappings} priceAlerts={priceAlerts} />
                    </Box>
                </Box>
            )}
        </Box>
    )
}
